import React from "react";
import { PromptMessageContainerProps, MessageType } from "../types";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { Loading } from "@/ui/Layout/Loading";
import { isJsonString } from "@/utils";
import { looksLikeData } from "./hiddenData";
export { looksLikeData };
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm"; // tables, strikethrough, task lists (CoPilot answers use tables)
import { markdownComponents } from "./markdown";
import Chip from "@/ui/Chip";
import { Variants } from "@/ui/types";
import { jsonrepair } from "jsonrepair";
import ReactJson from "react-json-view";

export const NiceBadge = ({ caption = "", content = "", isLoading = false, icon = <>🛠</>, onClick = () => {}, tooltip = "" }) => {
  return (
    <div
      // variant={Variants.BLACK}
      // className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-80 text-black/50 dark:text-white/50"
      className="cursor-pointer text-black/50 dark:text-white/50 hover:text-black dark:hover:text-white"
      onClick={onClick}
      title={tooltip}
      >
        <div title={content} className="text-sm flex items-center gap-1">
          {isLoading ? <Loading type="tiny" className="scale-50" /> : icon}
          <span className="text-sm">
            {(caption.includes("_") || caption.includes("-")) ? caption.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : caption}
          </span>
        </div>
    </div>
  );
}

const NotFoundArtifact = (props: any) => {
  const { content = "", name = "", payload, message, artifactOptions, setArtifactOptions, currentThread } = props; 
  const isLoading = content.includes('{') || content.includes('<') || isJsonString(content) || Array.isArray(content);
  const { artifact } = message;

  // return <div>{JSON.stringify(payload)}</div>;

  if (payload) {
    // if (payload.artifactId === "cot-message") return <div>{payload.message}</div>;
    // if (payload.artifactId === "section-spec") return null; // <div><NiceBadge caption="Section Spec" /><p>{payload.prompt}</p></div>;
    // if (payload?.component) return <NiceBadge caption={`Building: ${payload.component?.type}`} isLoading={isLoading} onClick={() => {
    //   // scroll to the the component in the canvas
    //   document.getElementById(payload?.component?.id)?.scrollIntoView({ behavior: "smooth" });
    // }} />; 
    // return <ReactJson src={payload} name={payload?.artifactId} />
    return null;
  }

  return <NiceBadge caption={name} content={content} isLoading={isLoading} tooltip={JSON.stringify(props, null, 2)} />;
};

export const MessageItem = (props: any) => {
  const { message = "" } = props;
  return (
    <div className="flex my-1 py-2">
      <div>
        <div
          className="dark:text-white/80 text-black/80 chat-message-text text-[1.0rem]"
          style={{
            fontFamily:
              "ui-sans-serif, -apple-system, system-ui, Segoe UI, Helvetica, Apple Color Emoji, Arial, sans-serif, Segoe UI Emoji, Segoe UI Symbol",
          }}
        >
          <Markdown
            children={String(message)}
            remarkPlugins={[remarkGfm, remarkBreaks]}
            rehypePlugins={[rehypeRaw]}
            components={markdownComponents}
          />
        </div>
      </div>
    </div>
  );
}

export const PromptMessageContainer: React.FC<PromptMessageContainerProps> = ({
  // type = "Minimal",
  messages = [],
  withFeedbackButtons = false,
  feedbackButtons = [],
  withIcon = true,
  artifacts = [],
  // setTopBarButtons = () => [],
  // shouldOpenArtifactWithThisOnFinish = {},
  artifactOptions = {},
  setArtifactOptions = () => {},
  // setShouldOpenArtifactWithThisOnFinish = () => {},
  isLoading = false,
  currentThread,
  setPinnedMessagesBottom = () => {},
}) => {

  // Scroll to the bottom of the chat conversation container when the messages change
  // useEffect(() => {
  //   const wn = document.getElementById("chat-conversation-container");
  //   wn?.scrollTo({ top: wn.scrollHeight });
  // }, []);

  // Render a single message
  const renderMessage = (message: MessageType, index: number) => {
    let {
      caption,
      from,
      // type,
      id,
      icon,
      // toolInvocations = [],
      name,
      // tool_call_id,
      run_id,
    } = message;
    const messageCaptionTestId = "message-caption";
    const messageIconTestId = "message-icon";

    const isFromUser = from === "User";

    if (!caption) return null;
    if (isJsonString(caption) || caption.includes('```json')) return null;

    return (
      <div
        key={index}
        className={`rounded-md text-md
        ${
          isFromUser
            ? "flex flex-row my-1 px-4 py-8 sm:px-6 bg-lightwhite dark:bg-[#161616]/80"
            : "flex my-1 px-4 py-2"
        }
      `}
      >
        {/* Render the message icon */}
        {withIcon && icon && (
          <div data-testid={messageIconTestId}>
            {typeof icon === "string" ? (
              <img
                src={icon}
                className="mr-2 flex h-8 w-8 rounded-full sm:mt-4"
              />
            ) : (
              icon
            )}
          </div>
        )}

        {/* Render the message caption */}
        {caption && (
          <div
            data-testid={messageCaptionTestId}
          >
            <div
              className="dark:text-white/80 text-black/80 chat-message-text text-[1.0rem]"
              style={{
                fontFamily:
                  "ui-sans-serif, -apple-system, system-ui, Segoe UI, Helvetica, Apple Color Emoji, Arial, sans-serif, Segoe UI Emoji, Segoe UI Symbol",
              }}
            >

              {/* Show a loader if the caption contains partial/broken JSON */}
              <Markdown
                children={String(caption)}
                remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[rehypeRaw]}
                components={markdownComponents}
              />
              {/* <div style={{ fontSize: 16 }}>{JSON.stringify(caption)}</div> */}
              {withFeedbackButtons && !isFromUser && renderFeedbackButtons(id, caption, run_id)}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render a structured message
  const renderStructuredMessage = (message: MessageType) => {
    const { caption, name } = message;
    let json;
    try { 
      json = JSON.parse(jsonrepair(caption));
    } catch (error) {
      // console.error("Error parsing JSON:", error);
      return null;
    }

    // return JSON.stringify(json);
    
    // return <ReactJson src={json} name={name} collapsed />;
    if (!json?.artifactId) return null;
    const artifactPayload = artifacts.find((artifact) => artifact.id === json.artifactId); // find a registered artifact by name

    const ArtifactComponent = artifactPayload?.component || NotFoundArtifact;
    return (
      <div className="px-4 chat-message-text text-[1.0rem]">
        <ArtifactComponent 
          payload={json} 
          message={message} 
          artifactOptions={artifactOptions} 
          setArtifactOptions={setArtifactOptions} 
          name={artifactPayload?.title || name} 
          currentThread={currentThread}
          setPinnedMessagesBottom={setPinnedMessagesBottom}
          allMessages={messages}
        />
      </div>
    );
  };

  const renderToolCall = (message: MessageType, index: number) => {
    const { caption = [], id, run_id, tool_calls = [] } = message;
    // return <div>Hi! {JSON.stringify(message)}</div>;

    // Only render if at least one tool_call artifact matches an incoming tool call's name
    if (
      !(
        tool_calls?.length &&
        tool_calls.some((call) =>
          artifacts.some(
            (artifact) =>
              artifact?.type === "tool_call" && artifact?.id === call.name
          )
        )
      )
    ) {
      return null;
    }

    return (
      <div className="rounded-md px-4 dark:text-white/80 text-black/80 chat-message-text text-[1.0rem]">
        <div className="flex flex-col gap-2">
          {tool_calls?.length > 0 && tool_calls?.map((item: any) => {
            // const { type, name, input, tool_call_id } = item;
            const { name, args } = item;
            const tool_call_id = item.id;
            const input = args;

            const artifactPayload = artifacts.filter((itm) => itm?.type === "tool_call").find((itm) => itm.id === name); // find a registered artifact by name
            const ArtifactComponent = artifactPayload?.component || NotFoundArtifact;

            let artifactArgs;
            if (isJsonString(input)) {
              artifactArgs = JSON.parse(jsonrepair(input));
            } else {
              artifactArgs = {
                // content: input,
                ...input,
                name: artifactPayload?.title || name,
              };
            }
            return <div key={tool_call_id} className="text-black/80">
              {/* <ReactJson src={item} name={tool_call_id} collapsed /> */}
              <ArtifactComponent 
                name={name} 
                payload={artifactArgs} 
                message={message} 
                artifactOptions={artifactOptions} 
                setArtifactOptions={setArtifactOptions} 
                currentThread={currentThread} 
                setPinnedMessagesBottom={setPinnedMessagesBottom} 
                allMessages={messages}
              />
            </div>
          })}
        </div>
        {/* {withFeedbackButtons && renderFeedbackButtons(String(id), JSON.stringify(caption), String(run_id))} */}
      </div>
    );
  }

  // Render a tool message
  const renderToolMessage = (message: MessageType, index: number) => {
    const { caption, name, artifact, id, run_id } = message;
    const artifactPayload = artifacts.filter((itm) => itm?.type !== "tool_call").find((itm) => itm.id === name); // find a registered artifact by name
    const ArtifactComponent = artifactPayload?.component || NotFoundArtifact;

    let artifactArgs;
    if (isJsonString(caption)) {
      artifactArgs = JSON.parse(jsonrepair(caption));
    } else {
      artifactArgs = { content: caption, name: artifactPayload?.title || name, payload: artifact };
    }

    if (artifactPayload?.hideMessage) return null;

    return (
      <div
        className="rounded-md px-4 dark:text-white/80 text-black/80 chat-message-text text-[1.0rem]"
        key={index}
        style={{
          fontFamily:
            "ui-sans-serif, -apple-system, system-ui, Segoe UI, Helvetica, Apple Color Emoji, Arial, sans-serif, Segoe UI Emoji, Segoe UI Symbol",
        }}
      >
        <ArtifactComponent
          name={name}
          {...artifactArgs}
          message={message}
          artifactOptions={artifactOptions}
          setArtifactOptions={setArtifactOptions}
          currentThread={currentThread}
          setPinnedMessagesBottom={setPinnedMessagesBottom}
          allMessages={messages}
        />
        <p className="chat-message-text text-[1.0rem] py-3">{caption}</p>
        {withFeedbackButtons && renderFeedbackButtons(id, caption, run_id)}
      </div>
    );
  }

  // Render the feedback buttons
  const renderFeedbackButtons = (id: string, caption = "", run_id: string) => {
    return (
      <div className="gap-1 flex opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        {feedbackButtons.map((button, index) => {
          const { label, onClick, className = "", icon = <></>, tooltip } = button;
          const feedbackButtonTestId = "feedback-button";

          return (
            <button
              key={index}
              data-testid={feedbackButtonTestId}
              onClick={() => onClick?.(run_id, caption)}
              title={tooltip}
              className={className}
            >
              {icon}
              {label}
            </button>
          );
        })}
      </div>
    );
  };

  // Render the prompt message container
  return (
    <div data-testid="PromptMessageContainer">
      {/* Render the message bubbles or captions based on the type prop. */}
        <div className="flex-1 gap-1 flex flex-col overflow-y-auto text-sm leading-6 text-slate-900 dark:text-lightwhite sm:text-base sm:leading-7">
          {/* don't send any json messages (that represents page content and we don't want to render those) */}
          {messages
            .map((message, index) => {
              const { caption = "", tool_calls = [], run_id = "", id = "", from = "", artifact = "" } = message;
              // console.log("PromptMessageContainer", "message", message);
              const isJson = looksLikeData(caption);

              // return <ReactJson src={{isJson, ...message}} name={String(index)} collapsed />;

              return (
                <div className="group relative" key={index}>
                  {/* Shows the index of the message in the chat conversation */}
                  {/* <span className="absolute top-0 right-0" onClick={() => console.log(message)}><NiceBadge icon={<span>{String(index)}</span>} /></span> */}
                  
                  {/* If caption is an array, loop through and renderMessage for each item with a "text" key */}                  
                  {Array.isArray(caption) && caption.some((item: any) => item?.text) &&
                    caption.map((item, arrIdx) =>
                      item?.text
                        ? renderMessage({ ...message, caption: item.text }, index + arrIdx)
                        : null
                    )
                  }
                  
                  {isJson && renderStructuredMessage(message)}
                  {!isJson && message.from === "Tool" && renderToolMessage(message, index)}
                  {message.from === "Bot" && (caption?.[0]?.type === "tool_use" || tool_calls?.length > 0) && renderToolCall(message, index)}
                  {!isJson && message.from !== "Tool" && renderMessage(message, index)}
                </div>
              );
            })}

          {isLoading && (
            <div className="flex items-center justify-start py-2">
              <span className="scale-75"><Loading type="tiny" /></span>
              <span className="ml-2 text-sm text-gray-500">Thinking...</span>
            </div>
          )}
        </div>
    </div>
  );
};

export default PromptMessageContainer;
